import {
  Box,
  Heading,
  HStack,
  Icon,
  Show,
  Text,
  ThemeColorPath,
  ThemeColorTextPath,
  ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {ConfiguredFinancingMatrixCellState} from './types/FinancingMatrixCellState';

interface MatrixCellTooltipValue {
  label: string;
  value: string;
}

interface MatrixCellTooltipProps {
  context: string;
  headlineLabel: string;
  headlineValue: string;
  summary?: ReactNode;
  values: MatrixCellTooltipValue[];
  statuses?: {
    label: string;
    state: ConfiguredFinancingMatrixCellState;
  }[];
}

export function MatrixCellTooltip(props: MatrixCellTooltipProps) {
  return (
    <Box width={92} padding={2} data-testid="matrix-cell-tooltip-content">
      <VStack spacing={4}>
        <Show when={isNotNilOrEmpty(props.statuses)}>
          <HStack spacing={3} wrap>
            {props.statuses?.map((status) => {
              const presentation = getStatusPresentation(status.state);

              return (
                <HStack key={status.label} spacing={1} align="center">
                  <Icon value={presentation.icon} color={presentation.iconColor} size={5} />
                  <Text size="small" color={presentation.textColor} alternative>
                    {status.label}
                  </Text>
                </HStack>
              );
            })}
          </HStack>
        </Show>

        <Text size="small" color="tertiary">
          {props.context}
        </Text>

        <VStack spacing={2}>
          <HStack spacing={2} align="center" wrap>
            <Heading size={1} color="white">
              {props.headlineValue}
            </Heading>
            <Text color="white">{props.headlineLabel}</Text>
          </HStack>
          <Show when={props.summary}>
            <Text size="small" color="tertiary">
              {props.summary}
            </Text>
          </Show>
        </VStack>

        <Box borderTop="1px solid" borderColor="palettes.neutral.500.100" paddingTop={2}>
          <VStack spacing={1}>
            {props.values.map((item) => (
              <HStack key={item.label} justify="space-between" spacing={4}>
                <Text size="small" color="tertiary">
                  {item.label}
                </Text>
                <Text size="small" color="white" alternative>
                  {item.value}
                </Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}

interface StatusPresentation {
  icon: ThemeIconKey;
  iconColor: ThemeColorPath;
  textColor: ThemeColorTextPath;
}

function getStatusPresentation(state: ConfiguredFinancingMatrixCellState) {
  return match<ConfiguredFinancingMatrixCellState, StatusPresentation>(state)
    .with('ACTIVE', () => ({
      icon: 'toggle/star',
      iconColor: 'severity.warning',
      textColor: 'warning',
    }))
    .with('CUSTOM', () => ({
      icon: 'image/edit',
      iconColor: 'palettes.purple.40.100',
      textColor: 'tertiary',
    }))
    .with('CONFIGURED', () => ({
      icon: 'action/check_circle',
      iconColor: 'palettes.blue.40.100',
      textColor: 'tertiary',
    }))
    .exhaustive();
}
